import { useState } from 'react';
import { UseFormReset } from 'react-hook-form';

import { useAuthUser } from 'auth/hooks';
import { NotificationPosition } from 'core/enums';
import { FormProvider, useAgGridClientTableContext } from 'core/states';
import { useGetUoms } from 'parts/hooks';
import QuotePartResetPricingForm from 'quotes/components/QuotePartResetPricingForm';
import { QuotePartHelper } from 'quotes/helpers';
import { useQuotePartActions, useRefreshQuotePartRows } from 'quotes/hooks';
import { useCustomerQuotePartsContext } from 'quotes/states';
import { TaxonomySource } from 'taxonomy/enums';

import { QUOTE_PART_UPDATE_BULK_FIELD_ORDER, QuotePartUpdateBulkField } from './enums';
import { mapQuotePartUpdateBulkFormData } from './mappers';
import { QuotePartUpdateBulkFormView } from './QuotePartUpdateBulkFormView';
import { quotePartUpdateBulkSchema } from './schema';
import { QuotePartUpdateBulkFormData } from './types';

interface IProps {
  isOpen: boolean;
  onClose: (closeSelection?: boolean) => void;
  onSubmitSuccess: () => void;
  isAlternateVersionEnabled: boolean;
}

export const QuotePartUpdateBulkForm = ({
  isOpen,
  onClose,
  onSubmitSuccess,
  isAlternateVersionEnabled,
}: IProps) => {
  const [isOpenResetPricingForm, setIsOpenResetPricingForm] = useState(false);
  const [resetPricingFormPayload, setResetPricingFormPayload] = useState(null);

  const { user } = useAuthUser();

  const { selectedRows, updateRows } = useAgGridClientTableContext();
  const { currentQuoteVersion } = useCustomerQuotePartsContext();
  const refreshQuotePartRows = useRefreshQuotePartRows(
    selectedRows[0]?.quoteId,
    currentQuoteVersion
  );

  const { upsertQuoteParts } = useQuotePartActions({
    notificationPosition: NotificationPosition.FORM,
  });

  const { records: uoms } = useGetUoms({ filters: { bomDisplay: 1 } }, isOpen);

  const onSubmit =
    (reset: UseFormReset<QuotePartUpdateBulkFormData>) =>
    async (data: QuotePartUpdateBulkFormData) => {
      setResetPricingFormPayload({ quoteParts: selectedRows.map((row) => row.id) });
      handleOnClose(reset, true);

      const { affectedQuotePartIds, success, quoteParts } = await upsertQuoteParts(
        mapQuotePartUpdateBulkFormData(selectedRows, data),
        { onSuccess: onSubmitSuccess }
      );

      if (!success) {
        return;
      }

      if (data.editQuantity || data.editUom) {
        setIsOpenResetPricingForm(true);
      }

      updateRows({ update: quoteParts }, { redrawRows: true });
      refreshQuotePartRows(affectedQuotePartIds);
    };

  const getEditableFields = () => {
    let editableFields = [...QUOTE_PART_UPDATE_BULK_FIELD_ORDER];

    const isTaxonomyStepEnabled =
      isAlternateVersionEnabled &&
      (user?.companySettings?.taxonomyEnabled || false) &&
      !selectedRows.some((row) => row.taxonomySource === TaxonomySource.EXTERNAL);

    editableFields = editableFields.filter(
      (field) => field !== QuotePartUpdateBulkField.TAXONOMY || isTaxonomyStepEnabled
    );

    const atLeastOneVerifiedQuotePartSelected = selectedRows.some((row) => row.verified);

    // Manufacturer is not editable for verified parts
    if (atLeastOneVerifiedQuotePartSelected) {
      editableFields = editableFields.filter(
        (field) => field !== QuotePartUpdateBulkField.MANUFACTURER
      );
    }

    const allPartsAllowReplacementCost =
      selectedRows.length > 0 &&
      selectedRows.every((row) =>
        QuotePartHelper.allowOverrideReplacementCost({
          isAlternateVersionEnabled,
          isShipAndDebitVendor: row.supplier?.isShipAndDebit ?? false,
          shipmentTypeCode: row.shipmentType?.code,
        })
      );

    if (!allPartsAllowReplacementCost) {
      editableFields = editableFields.filter(
        (field) =>
          field !== QuotePartUpdateBulkField.REPLACEMENT_COST &&
          field !== QuotePartUpdateBulkField.REPLACEMENT_COST_NUMBER
      );
    }

    if (!isAlternateVersionEnabled) {
      editableFields = editableFields.filter(
        (field) =>
          field !== QuotePartUpdateBulkField.PRIORITIZE_LEGACY_COST &&
          field !== QuotePartUpdateBulkField.PRICING_DATE &&
          field !== QuotePartUpdateBulkField.PRICE_BASED_ON &&
          field !== QuotePartUpdateBulkField.PRICING_STRATEGY
      );
    }

    return editableFields;
  };

  const handleOnClose = (
    reset?: UseFormReset<QuotePartUpdateBulkFormData>,
    closeSelection = false
  ) => {
    reset();
    onClose(closeSelection);
  };

  return (
    <FormProvider
      schema={quotePartUpdateBulkSchema(user.companySettings, isAlternateVersionEnabled)}
    >
      <QuotePartUpdateBulkFormView
        isOpen={isOpen}
        onSubmit={onSubmit}
        onClose={handleOnClose}
        uoms={uoms}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        editableFields={getEditableFields()}
        selectedQuoteParts={selectedRows}
      />

      <QuotePartResetPricingForm
        isOpen={isOpenResetPricingForm}
        onClose={() => setIsOpenResetPricingForm(false)}
        resetPricingFormPayload={resetPricingFormPayload}
      />
    </FormProvider>
  );
};
